/**
 * Cloud-synced storage for the Monthly Sale Incentive page configuration
 * (brand cuts, minimums, rates, and the Staff-by-brand list).
 *
 * Shape per branch document:
 *   {
 *     brandCuts:       { [brandId]: { oe?: string, ord?: string } },
 *     mins:            { [brandId]: { oeMin?: string, ordMin?: string } },
 *     miscDivisor:     string,
 *     oeRatePct:       string,
 *     ordRatePct:      string,
 *     staff:           Array<staffRow>,
 *     deductParties:   Array<{ partyName: string, id?: string }>,
 *     sparewayBrTransferOe:  string,
 *     sparewayBrTransferOrd: string,
 *     sparewayOeMinSale:     string,
 *     sparewayOrdMinSale:    string,
 *     sparewayBrandSales:    { [brandId]: { oe?: string, ord?: string } },
 *     accountantPhone: string,
 *     updatedAt:       Firestore serverTimestamp,
 *     updatedBy?:      string,
 *   }
 *
 * Firestore path: monthlyIncentiveConfig/{branchId}
 */
import { doc, getDoc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../firebase";

const COLLECTION = "monthlyIncentiveConfig";

/** Branch values we use ("Main", "Branch 2", "Branch 3") are safe doc IDs.
 *  This guards against any future branch values that contain "/" or empty. */
function safeBranchId(branch) {
  const s = String(branch || "").trim() || "Main";
  return s.replace(/\//g, "_");
}

function readUserId() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return "";
    const u = JSON.parse(raw) || {};
    return String(u.username || u.email || u.uid || u.id || "").trim();
  } catch {
    return "";
  }
}

/** One-shot fetch of a branch config. Returns the doc data (without server
 *  metadata) or `null` if the document does not yet exist.
 *  Throws on Firestore error so callers can decide between "no cloud copy" and
 *  "could not reach cloud". */
export async function fetchIncentiveConfig(branch) {
  const ref = doc(db, COLLECTION, safeBranchId(branch));
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const data = snap.data() || {};
  // Strip Firestore-only fields the page does not need
  const { updatedAt: _ua, updatedBy: _ub, ...rest } = data;
  return rest;
}

/** Subscribe to a branch config. Returns an unsubscribe function.
 *  `onUpdate(data | null)` is called whenever the doc changes;
 *  `onError(err)` is called on Firestore errors. */
export function subscribeIncentiveConfig(branch, onUpdate, onError) {
  const ref = doc(db, COLLECTION, safeBranchId(branch));
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onUpdate?.(null);
        return;
      }
      const data = snap.data() || {};
      const { updatedAt: _ua, updatedBy: _ub, ...rest } = data;
      onUpdate?.(rest);
    },
    (err) => {
      console.error("[monthlyIncentiveConfig] subscribe error", err);
      onError?.(err);
    }
  );
}

/** Write the branch config to Firestore (merge). */
export async function saveIncentiveConfig(branch, cfg) {
  const ref = doc(db, COLLECTION, safeBranchId(branch));
  const payload = {
    ...cfg,
    updatedAt: serverTimestamp(),
    updatedBy: readUserId() || null,
  };
  await setDoc(ref, payload, { merge: true });
}

/** Convenience: write only the accountant phone field. */
export async function saveAccountantPhone(branch, phone) {
  const ref = doc(db, COLLECTION, safeBranchId(branch));
  await setDoc(
    ref,
    {
      accountantPhone: String(phone ?? ""),
      updatedAt: serverTimestamp(),
      updatedBy: readUserId() || null,
    },
    { merge: true }
  );
}
